import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { X, Camera, Warning } from "@phosphor-icons/react";

/**
 * QrScanner — fullscreen camera overlay that reads a QR code and returns
 * the raw text via `onDetect(text)`. The parent is responsible for
 * resolving the text against the backend (`POST /vehicles/resolve-qr`).
 *
 * Uses html5-qrcode which handles getUserMedia, decoding and torch itself.
 */
export default function QrScanner({ open, onClose, onDetect }) {
    const readerRef = useRef(null);
    const scannerRef = useRef(null); // Html5Qrcode instance
    const [err, setErr] = useState("");
    const [cameras, setCameras] = useState([]);
    const [currentCam, setCurrentCam] = useState("");

    useEffect(() => {
        if (!open) return;
        setErr("");
        let cancelled = false;

        (async () => {
            try {
                const devices = await Html5Qrcode.getCameras();
                if (cancelled) return;
                if (!devices || devices.length === 0) {
                    setErr("使用可能なカメラが見つかりません");
                    return;
                }
                setCameras(devices);
                // Prefer the back camera on mobile phones.
                const back = devices.find((d) => /back|rear|environment/i.test(d.label));
                const chosen = back?.id || devices[devices.length - 1].id;
                setCurrentCam(chosen);
            } catch (e) {
                setErr("カメラへのアクセスが拒否されました。ブラウザ設定を確認してください。");
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [open]);

    useEffect(() => {
        if (!open || !currentCam) return;
        let cancelled = false;

        (async () => {
            try {
                const scanner = new Html5Qrcode("qr-reader-region", { verbose: false });
                scannerRef.current = scanner;
                await scanner.start(
                    currentCam,
                    { fps: 10, qrbox: { width: 260, height: 260 }, aspectRatio: 1.0 },
                    (decoded) => {
                        if (cancelled) return;
                        onDetect(decoded);
                    },
                    () => {}, // ignore per-frame decode errors
                );
            } catch (e) {
                setErr(`カメラを起動できませんでした: ${e?.message || e}`);
            }
        })();

        return () => {
            cancelled = true;
            const s = scannerRef.current;
            scannerRef.current = null;
            if (!s) return;
            // Only clear() after stop() has resolved AND only when the
            // library reports it is not actively scanning. Calling clear()
            // while a scan is ongoing throws a benign but noisy dev-overlay
            // warning.
            const finalize = () => {
                try { s.clear(); } catch (_) { /* ignore */ }
            };
            s.stop().then(finalize).catch(finalize);
        };
    }, [open, currentCam]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[90] bg-black flex flex-col" data-testid="qr-scanner-overlay">
            <div className="flex items-center justify-between p-3 text-white">
                <div className="flex items-center gap-2">
                    <Camera size={24} />
                    <span className="font-semibold">QRコードをスキャン</span>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    className="prc-btn-ghost !bg-white/10 !text-white h-9 w-9 inline-flex items-center justify-center"
                    aria-label="閉じる"
                    data-testid="qr-scanner-close"
                >
                    <X size={16} />
                </button>
            </div>

            <div className="flex-1 flex items-center justify-center p-4 relative">
                <div id="qr-reader-region" ref={readerRef} className="w-full max-w-md" />
                {err && (
                    <div className="absolute inset-4 flex flex-col items-center justify-center text-center bg-black/70 rounded-sm p-6 gap-2">
                        <Warning size={24} className="text-[var(--prc-amber)]" />
                        <p className="text-white text-sm">{err}</p>
                    </div>
                )}
            </div>

            <div className="p-4 text-center text-xs text-white/70">
                <p>車両に貼られたQRコードを枠内に映してください</p>
                {cameras.length > 1 && (
                    <select
                        value={currentCam}
                        onChange={(e) => setCurrentCam(e.target.value)}
                        className="mt-2 bg-white/10 text-white text-xs px-2 py-1 rounded-sm"
                        data-testid="qr-scanner-camera-select"
                    >
                        {cameras.map((c) => (
                            <option key={c.id} value={c.id}>{c.label || `カメラ ${c.id.substring(0, 6)}`}</option>
                        ))}
                    </select>
                )}
            </div>
        </div>
    );
}
